import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchParameter, U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U3155';

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  EXPORT_EXCEL: string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  EXPORT_EXCEL: `${LOGICALUNIT_URL}/exportExcel`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

export interface U3155FamilyTranslation {
  id?: string;
  languageCode: string;
  familyLabel: string;
  version?: number;
}

export interface U3155FamilyItem {
  id: string;
  familyCode?: string;
  sortOrder?: number;
  translations?: U3155FamilyTranslation[];
}

export interface U3155FamilyRef {
  id: string;
  familyCode?: string;
  translations?: U3155FamilyTranslation[];
}

/** Réponse GET /U3155/initLU + logicalUnitCode fusionné côté shell. */
export interface U3155InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
  /** Familles actives (BL-0694). */
  families?: U3155FamilyItem[];
}

/** Ligne POST /U3155/search. */
export interface U3155MinutesWorkNatureSearchRow {
  id: string;
  workNatureCode?: string;
  active?: boolean;
  workNatureLabelFr?: string;
  workNatureLabelEn?: string;
  family?: U3155FamilyRef | null;
  familyLabelFr?: string | null;
  familyLabelEn?: string | null;
  version: number;
  deletedAt?: string | Date | null;
  createdAt?: string | Date;
}

export interface U3155MinutesWorkNatureTranslation {
  id?: string;
  languageCode: string;
  workNatureLabel: string;
  version?: number;
}

/** Détail GET /U3155/:id (result). */
export interface U3155MinutesWorkNature {
  id: string;
  workNatureCode: string;
  active: boolean;
  translations: U3155MinutesWorkNatureTranslation[];
  family?: U3155FamilyRef | null;
  version: number;
}

export type U3155MinutesWorkNatureTranslationFormRow = U3155MinutesWorkNatureTranslation;

export type U3155MinutesWorkNatureFormRow = U3155MinutesWorkNature;

export interface U3155MinutesWorkNatureCreateBody {
  workNatureCode: string;
  active: boolean;
  family?: { id: string } | null;
  translations: Array<{
    languageCode: string;
    workNatureLabel: string;
  }>;
}

export interface U3155MinutesWorkNatureUpdateBody {
  id: string;
  active: boolean;
  version: number;
  family?: { id: string } | null;
  translations: Array<
    | {
        languageCode: string;
        workNatureLabel: string;
      }
    | {
        id: string;
        languageCode: string;
        workNatureLabel: string;
        version: number;
      }
  >;
}

export interface U3155MinutesWorkNatureDeleteBody {
  id: string;
  version: number;
}

export const U3155_ERROR_CODE_DUPLICATE = 'BU315501';
export const U3155_ERROR_CODE_IN_USE = 'BU315502';
export const U3155_ERROR_CODE_FAMILY_INVALID = 'BU315503';

export function createEmptyU3155FormRow(): U3155MinutesWorkNatureFormRow {
  return {
    id: '',
    workNatureCode: '',
    active: true,
    version: 0,
    family: null,
    translations: [
      { languageCode: 'fr', workNatureLabel: '' },
      { languageCode: 'en', workNatureLabel: '' },
    ],
  };
}

export function u3155ResolveFamilyId(family: U3155FamilyRef | U3155FamilyItem | null | undefined): string | undefined {
  if (!family?.id?.trim()) return undefined;
  return family.id.trim();
}

function u3155FamilyTranslationLabel(
  family: U3155FamilyRef | U3155FamilyItem | null | undefined,
  lang: 'fr' | 'en',
): string | undefined {
  if (!family?.translations?.length) return undefined;
  const tr =
    family.translations.find((t) => t.languageCode?.trim().toLowerCase() === lang) ??
    family.translations.find((t) => t.languageCode?.trim().toLowerCase() === 'fr');
  const desc = tr?.familyLabel?.trim();
  return desc || undefined;
}

/** Libellé famille pour affichage (langue active). */
export function u3155ResolveFamilyLabel(
  family: U3155FamilyRef | U3155FamilyItem | null | undefined,
  languageCode: string,
  familyLabelFr?: string | null,
  familyLabelEn?: string | null,
): string {
  const isFr = languageCode.startsWith('fr');
  const fromSearch = (isFr ? familyLabelFr : familyLabelEn)?.trim() || familyLabelFr?.trim() || familyLabelEn?.trim();
  if (fromSearch) return fromSearch;
  const fromTr = u3155FamilyTranslationLabel(family, isFr ? 'fr' : 'en');
  if (fromTr) return fromTr;
  return family?.familyCode?.trim() ?? '';
}

export function normalizeU3155FromApi(row: U3155MinutesWorkNature): U3155MinutesWorkNature {
  return {
    ...row,
    active: row.active !== false,
    translations: Array.isArray(row.translations)
      ? row.translations.map((tr) => ({
          ...tr,
          languageCode: typeof tr.languageCode === 'string' ? tr.languageCode.trim().toLowerCase() : tr.languageCode,
        }))
      : [],
  };
}

function u3155TranslationDescription(
  translations: U3155MinutesWorkNatureTranslation[] | undefined,
  lang: 'fr' | 'en',
): string | undefined {
  if (!Array.isArray(translations)) return undefined;
  const tr = translations.find((t) => t.languageCode?.trim().toLowerCase() === lang);
  const desc = tr?.workNatureLabel?.trim();
  return desc || undefined;
}

/** Reconstruit une ligne search (descriptions FR/EN) à partir du détail fiche — évite la perte d'affichage liste après GET/save. */
export function u3155FormRowToSearchRow(
  row: U3155MinutesWorkNature | U3155MinutesWorkNatureFormRow,
  existing?: U3155MinutesWorkNatureSearchRow | null,
): U3155MinutesWorkNatureSearchRow {
  const searchRow: U3155MinutesWorkNatureSearchRow = {
    id: row.id,
    workNatureCode: row.workNatureCode,
    active: row.active !== false,
    workNatureLabelFr: u3155TranslationDescription(row.translations, 'fr'),
    workNatureLabelEn: u3155TranslationDescription(row.translations, 'en'),
    family: row.family ?? null,
    familyLabelFr: u3155FamilyTranslationLabel(row.family, 'fr') ?? existing?.familyLabelFr ?? null,
    familyLabelEn: u3155FamilyTranslationLabel(row.family, 'en') ?? existing?.familyLabelEn ?? null,
    version: row.version,
  };
  if (existing) {
    return { ...existing, ...searchRow };
  }
  return searchRow;
}

export function u3155FormatMinutesWorkNatureListLabel(row: U3155MinutesWorkNatureSearchRow, language: string): string {
  const code = row.workNatureCode?.trim() ?? '';
  const isFr = language.startsWith('fr');
  const linguistic =
    (isFr ? row.workNatureLabelFr : row.workNatureLabelEn)?.trim() ||
    row.workNatureLabelFr?.trim() ||
    row.workNatureLabelEn?.trim() ||
    '';
  if (code && linguistic) return `${code} — ${linguistic}`;
  return code || linguistic;
}

export function buildCreateBody(data: U3155MinutesWorkNatureFormRow): U3155MinutesWorkNatureCreateBody {
  const familyId = u3155ResolveFamilyId(data.family);
  return {
    workNatureCode: data.workNatureCode.trim(),
    active: data.active !== false,
    ...(familyId ? { family: { id: familyId } } : {}),
    translations: data.translations.map((tr) => ({
      languageCode: tr.languageCode.trim().toLowerCase(),
      workNatureLabel: tr.workNatureLabel.trim(),
    })),
  };
}

export function buildUpdateBody(data: U3155MinutesWorkNatureFormRow): U3155MinutesWorkNatureUpdateBody {
  const familyId = u3155ResolveFamilyId(data.family);
  return {
    id: data.id,
    active: data.active !== false,
    version: data.version,
    ...(familyId ? { family: { id: familyId } } : data.family === null ? { family: null } : {}),
    translations: data.translations.map((tr) => {
      const languageCode = tr.languageCode.trim().toLowerCase();
      const workNatureLabel = tr.workNatureLabel.trim();
      const hasId = Boolean(tr.id && String(tr.id).trim() !== '');
      if (hasId && tr.id) {
        return {
          id: tr.id,
          languageCode,
          workNatureLabel,
          version: typeof tr.version === 'number' ? tr.version : 0,
        };
      }
      return { languageCode, workNatureLabel };
    }),
  };
}

export function buildDeleteBody(data: U3155MinutesWorkNatureFormRow): U3155MinutesWorkNatureDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doExportExcel = (axios: AxiosInstance, filter: U2000SearchParameter, lang?: 'fr' | 'en') => {
  return axios.post(LogicalUnitEndpoints.EXPORT_EXCEL, { filter, lang });
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3155MinutesWorkNatureCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3155MinutesWorkNatureUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3155MinutesWorkNatureDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3155_ERROR_CODE_DUPLICATE:
      errorResult.fieldErrors['workNatureCode'] = t('errWorkNatureCodeDuplicate', { ns: nameSpace });
      break;
    case U3155_ERROR_CODE_IN_USE:
      errorResult.genericErrors.push({
        title: t('errorTitle', { ns: nameSpace }),
        text: t('errDeleteInUse', { ns: nameSpace }),
      });
      break;
    case U3155_ERROR_CODE_FAMILY_INVALID:
      errorResult.fieldErrors['family'] = t('errFamilyInvalid', { ns: nameSpace });
      break;
    default:
      break;
  }
}
